import React, { useState, useEffect, useRef, useCallback } from 'react';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import styles from './index.module.css';
import { useAuth } from '../../hooks/useAuth';
import { loginWithGoogle, loginWithFacebook } from '../../services/authService';

// ─── Letter Avatar ─────────────────────────────────────────────────────────────
// Shows the first letter of a name inside a coloured circle.
const AVATAR_COLORS = [
    '#1976d2','#388e3c','#f57c00','#7b1fa2',
    '#c62828','#00838f','#5d4037','#455a64',
];
function getColor(name) {
    if (!name) return '#9e9e9e';
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
function LetterAvatar({ name, size = 36 }) {
    const letter = name ? name.trim()[0].toUpperCase() : '?';
    const bg = getColor(name);
    return (
        <div style={{
            width: size, height: size, borderRadius: '50%',
            background: bg, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: size * 0.45,
            userSelect: 'none', flexShrink: 0,
        }}>
            {letter}
        </div>
    );
}

export default function LoginButton({ setInputDate, setIsMale, inputDate, isMale }) {
    const { user, bookmarks, loginSuccess, logout } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);

    // sub-view inside the menu: 'main' | 'addBookmark'
    const [subView, setSubView] = useState('main');
    const [newName, setNewName] = useState('');
    const [newRemark, setNewRemark] = useState('');
    const [addError, setAddError] = useState('');
    const [addLoading, setAddLoading] = useState(false);

    const panelRef = useRef(null);
    const googleInitialized = useRef(false);
    const googleBtnRef = useRef(null);

    // ─── Close panel on outside click ─────────────────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setMenuOpen(false);
                setSubView('main');
            }
        };
        if (menuOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [menuOpen]);

    // ─── Initialise Google GIS once on mount ──────────────────────────────────
    // We only call initialize(); the custom button triggers prompt() on click.
    // This avoids any request to accounts.google.com/gsi/button.
    const initGoogle = useCallback(() => {
        if (googleInitialized.current || !window.google?.accounts?.id) return;
        const clientId = window.googleClientId;
        if (!clientId) { console.warn('Google Client ID not loaded from config.json yet'); return; }
        googleInitialized.current = true;
        window.google.accounts.id.initialize({
            client_id: clientId,
            use_fedcm_for_prompt: true, // Use FedCM for One Tap prompt on supported browsers (e.g. Chrome 113+). Still falls back to popup on unsupported browsers (e.g. Safari).
            callback: async ({ credential }) => {
                try {
                    const userData = await loginWithGoogle(credential);
                    loginSuccess(userData);
                    setMenuOpen(false);
                } catch (err) {
                    console.error('Google login failed:', err);
                }
            },
        });
    }, [loginSuccess]);

    useEffect(() => {
        if (window.google?.accounts?.id) {
            initGoogle();
        } else {
            const interval = setInterval(() => {
                if (window.google?.accounts?.id) {
                    clearInterval(interval);
                    initGoogle();
                }
            }, 200);
            return () => clearInterval(interval);
        }
    }, [initGoogle]);

    // ─── Render the official Google button when the login panel opens ──────────
    // renderButton() opens a popup that works on ALL platforms, including iOS
    // Safari where the One Tap prompt() overlay is not supported.
    useEffect(() => {
        if (!menuOpen || user) return;
        const tryRender = () => {
            if (!googleBtnRef.current || !window.google?.accounts?.id) return false;
            initGoogle();
            window.google.accounts.id.renderButton(googleBtnRef.current, {
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                locale: 'zh-TW',
                width: '240',
            });
            return true;
        };
        if (!tryRender()) {
            // SDK not yet loaded — poll until it is
            const id = setInterval(() => { if (tryRender()) clearInterval(id); }, 200);
            return () => clearInterval(id);
        }
    }, [menuOpen, user, initGoogle]);

    // ─── Facebook login ────────────────────────────────────────────────────────
    const handleFacebookLogin = useCallback(() => {
        if (window.location.protocol !== 'https:') {
            alert('Facebook 登入需要 HTTPS 連線。\n請使用 HTTPS 網址開啟本網站後再試。');
            return;
        }
        if (!window.FB) { alert('Facebook SDK not loaded yet, please try again.'); return; }
        window.FB.login(async (response) => {
            if (response.authResponse) {
                try {
                    const userData = await loginWithFacebook(response.authResponse.accessToken);
                    loginSuccess(userData);
                    setMenuOpen(false);
                } catch (err) {
                    console.error('Facebook login failed:', err);
                    alert('Facebook 登入失敗：' + err.message);
                }
            }
        }, { scope: 'public_profile,email' });
    }, [loginSuccess]);

    // ─── Logout ────────────────────────────────────────────────────────────────
    const handleLogout = useCallback(async () => {
        await logout();
        setMenuOpen(false);
        setSubView('main');
    }, [logout]);

    // ─── Navigate to bookmark ──────────────────────────────────────────────────
    const handleNavigateToBookmark = useCallback((bookmark) => {
        if (!setInputDate || !setIsMale) return;
        // westernDatetime from server: "1990-05-15T08:30:00"
        // datetime-local input expects: "1990-05-15T08:30"
        const dt = bookmark.westernDatetime
            ? bookmark.westernDatetime.slice(0, 16)
            : '';
        setInputDate(dt);
        setIsMale(bookmark.isMale);
        setMenuOpen(false);
    }, [setInputDate, setIsMale]);

    // ─── Toggle menu ──────────────────────────────────────────────────────────
    const toggleMenu = () => {
        setMenuOpen(prev => !prev);
        setSubView('main');
    };

    // ─── Render ────────────────────────────────────────────────────────────────
    return (
        <div className={styles.container} ref={panelRef}>
            {/* Floating action button */}
            <button
                className={`${styles.floatingBtn} ${user ? styles.loggedIn : ''}`}
                onClick={toggleMenu}
                title={user ? user.displayName || '我的帳號' : '登入'}
            >
                {user
                    ? <LetterAvatar name={user.displayName || user.email} size={40} />
                    : <AccountCircleIcon style={{ fontSize: 32, color: '#888' }} />
                }
            </button>

            {/* Dropdown panel */}
            {menuOpen && (
                <div className={styles.panel}>
                    <button className={styles.closeBtn} onClick={() => setMenuOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </button>

                    {!user ? (
                        /* ── Guest: login options ── */
                        <div className={styles.loginOptions}>
                            <p className={styles.loginTitle}>選擇登入方式</p>
                            {/* Google renders its own button here — handles popup flow on all platforms including iOS */}
                            <div ref={googleBtnRef} className={styles.googleBtnContainer} />
                            {/*
                            {window.location.protocol === 'https:' ? (
                                <button className={styles.fbBtn} onClick={handleFacebookLogin}>
                                    <img
                                        src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg"
                                        alt="fb"
                                        style={{ width: 22, height: 22, marginRight: 8 }}
                                    />
                                    以 Facebook 登入
                                </button>
                            ) : (
                                <div className={styles.fbBtnDisabled} title="Facebook 登入需要 HTTPS 連線">
                                    <img
                                        src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg"
                                        alt="fb"
                                        style={{ width: 22, height: 22, marginRight: 8, opacity: 0.5 }}
                                    />
                                    Facebook 登入（需要 HTTPS）
                                </div>
                            )}
                            */}
                        </div>
                    ) : (
                        /* ── Logged in: user menu ── */
                        <>
                            {subView === 'main' && (
                                <UserMenu
                                    user={user}
                                    bookmarks={bookmarks}
                                    onNavigate={handleNavigateToBookmark}
                                    onLogout={handleLogout}
                                    onAddBookmark={() => {
                                        setNewName(''); setNewRemark(''); setAddError('');
                                        setSubView('addBookmark');
                                    }}
                                />
                            )}
                            {subView === 'addBookmark' && (
                                <AddBookmarkView
                                    newName={newName} setNewName={setNewName}
                                    newRemark={newRemark} setNewRemark={setNewRemark}
                                    addError={addError} setAddError={setAddError}
                                    addLoading={addLoading} setAddLoading={setAddLoading}
                                    inputDate={inputDate}
                                    isMale={isMale}
                                    onBack={() => setSubView('main')}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function UserMenu({ user, bookmarks, onNavigate, onLogout, onAddBookmark }) {
    return (
        <div className={styles.userMenu}>
            {/* Account info */}
            <div className={styles.accountRow}>
                <LetterAvatar name={user.displayName || user.email} size={40} />
                <div className={styles.accountInfo}>
                    <div className={styles.accountName}>{user.displayName || '—'}</div>
                    <div className={styles.accountEmail}>{user.email}</div>
                </div>
            </div>

            <hr className={styles.divider} />

            {/* Bookmarks */}
            <div className={styles.bookmarkHeader}>
                <BookmarksIcon fontSize="small" /> &nbsp;書籤
                <button className={styles.addBmBtn} onClick={onAddBookmark} title="新增書籤">
                    <AddIcon fontSize="small" />
                </button>
            </div>

            <div className={styles.bookmarkList}>
                {bookmarks.length === 0 && (
                    <div className={styles.noBookmark}>尚無書籤，可在查詢後新增。</div>
                )}
                {bookmarks.map(bm => (
                    <BookmarkRow key={bm.id} bookmark={bm} onNavigate={onNavigate} />
                ))}
            </div>

            <hr className={styles.divider} />

            {/* Logout */}
            <button className={styles.logoutBtn} onClick={onLogout}>
                <LogoutIcon fontSize="small" />&nbsp;登出
            </button>
        </div>
    );
}

function BookmarkRow({ bookmark, onNavigate }) {
    return (
        <div className={styles.bookmarkRow}>
            <BookmarkIcon fontSize="small" className={styles.bmIcon} />
            <div className={styles.bmInfo}>
                <span className={styles.bmName}>{bookmark.name || '(未命名)'}</span>
                {bookmark.remark && (
                    <span className={styles.bmRemark}>{bookmark.remark}</span>
                )}
                <span className={styles.bmDate}>{bookmark.westernDatetime
                    ? bookmark.westernDatetime.slice(0, 16).replace('T', ' ')
                    : ''} {bookmark.isMale ? '♂' : '♀'}
                </span>
            </div>
            <button
                className={styles.bmNavBtn}
                title="前往此書籤"
                onClick={() => onNavigate(bookmark)}
            >
                <ArrowForwardIcon fontSize="small" />
            </button>
        </div>
    );
}

function AddBookmarkView({
    newName, setNewName,
    newRemark, setNewRemark,
    addError, setAddError,
    addLoading, setAddLoading,
    inputDate,
    isMale,
    onBack,
}) {
    const { addBookmark } = useAuth();

    const handleAdd = async () => {
        setAddError('');
        if (!inputDate) {
            setAddError('請先選擇出生日期後再新增書籤。');
            return;
        }
        setAddLoading(true);
        try {
            await addBookmark({
                westernDatetime: inputDate,
                isMale: isMale !== undefined ? isMale : true,
                name: newName,
                remark: newRemark,
            });
            onBack();
        } catch (e) {
            setAddError(e.message);
        } finally {
            setAddLoading(false);
        }
    };

    return (
        <div className={styles.addBmView}>
            <div className={styles.addBmTitle}>
                <button className={styles.backBtn} onClick={onBack}>←</button>
                &nbsp;新增書籤
            </div>

            {/* Date / gender summary */}
            <div className={styles.bmDateSummary}>
                <span className={styles.bmDateSummaryLabel}>日期：</span>
                <span className={styles.bmDateSummaryValue}>
                    {inputDate
                        ? inputDate.slice(0, 16).replace('T', ' ')
                        : <span className={styles.bmDateSummaryMissing}>（未選擇）</span>}
                </span>
                <span className={styles.bmDateSummaryLabel} style={{ marginLeft: 10 }}>性別：</span>
                <span className={styles.bmDateSummaryValue}>
                    {isMale === undefined ? '—' : isMale ? '♂ 男' : '♀ 女'}
                </span>
            </div>

            <label className={styles.formLabel}>名稱</label>
            <input
                className={styles.formInput}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="請輸入名稱"
            />
            <label className={styles.formLabel}>備注</label>
            <input
                className={styles.formInput}
                value={newRemark}
                onChange={e => setNewRemark(e.target.value)}
                placeholder="選填備注"
            />
            {addError && <div className={styles.errorMsg}>{addError}</div>}
            <button
                className={styles.saveBtn}
                onClick={handleAdd}
                disabled={addLoading || !inputDate}
            >
                {addLoading ? '儲存中…' : '儲存'}
            </button>
        </div>
    );
}
